import { Tab } from '@/utils/tabs';
import { DownloadOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Badge, Button, Modal, Spin, Tabs, Tooltip } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import { setCompanies } from '../../../contextRedux/Company/CompanyStore';
import { setContract, updateField } from '../../../contextRedux/Contract/ContractModel';
import { setContractInterventionTariffTypes } from '../../../contextRedux/ContractInterventionTariffType/ContractInterventionTariffType';
import { setContractInterventionTypes } from '../../../contextRedux/ContractInterventionType/ContractInterventionType';
import { setDevices } from '../../../contextRedux/DeviceType/DeviceTypeStore';
import { setOffers } from '../../../contextRedux/Offer/OfferStore';
import { setStatusContractes } from '../../../contextRedux/StatusContract/StatusContractStore';
import { setSystemTypes } from '../../../contextRedux/SystemType/SystemTypeStore';
import { setUsers } from '../../../contextRedux/User/UserStore';
import CompanyService from '../../../services/company/CompanyService';
import ConfigurationService from '../../../services/configuration/ConfigurationService';
import ContractService from '../../../services/contract/ContractService';
import DeviceTypeService from '../../../services/deviceType/DeviceTypeService';
import OfferService from '../../../services/offer/OfferService';
import SystemTypeService from '../../../services/systemType/SystemTypeService';
import UserService from '../../../services/user/UserService';
import WorkPlaceService from '../../../services/workPlace/WorkPlaceService';
import { RootState } from '../../../store';
import { saveBlobCrossPlatform } from '../../../utils/saveBlobCrossPlatform';
import './NewContract.css';
import AddEquipmentTab from './Tabs/AddEquipmentTab/AddEquipmentTab';
import CompanyDetailsTab from './Tabs/CompanyDetailsTab/CompanyDetailsTab';
import InterventiiTab from './Tabs/Interventii/InterventiiTab';
import LaborTab from './Tabs/LaborTab/LaborTab';
import Mentenance from './Tabs/Mentenance/Mentenance';
import SummaryTab from './Tabs/SummaryTab/SummaryTab';

const NewContract: FC = () => {
  const { modal, message } = App.useApp();
  const isMobile = useMediaQuery({ maxWidth: 720 });
  // const isTab = useMediaQuery({ maxWidth: 920});

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const location = useLocation();

  const useQueryParams = () => {
    return new URLSearchParams(useLocation().search);
  };

  const query = useQueryParams();
  const [id, setId] = useState<string>(query.get('id') ?? '');

  const reduxContract = useSelector((state: RootState) => state?.models?.contract);

  const addedDevices = useSelector((state: RootState) => state?.models?.contract?.contractWorkPlaceItems);

  const [totalContractValue, setTotalContractValue] = useState<number>(0);

  const isShowAddEquipmentTab: boolean = !!(
    reduxContract?.companyId &&
    reduxContract?.accountManagerId &&
    reduxContract?.accountSaleId &&
    reduxContract?.enumContractStatusId &&
    reduxContract?.acquisitionType &&
    reduxContract?.date &&
    reduxContract?.dateStart &&
    reduxContract?.dateEnd &&
    reduxContract?.registrationNumber
  );

  const { confirm } = Modal;

  const { data: _accountSales, isFetching: isUsersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () =>
      UserService.search({
        isActive: true,
      }).then((data) => {
        dispatch(setUsers(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    const idFromUrl = query.get('id');
    if (!idFromUrl) {
    }
    setId(idFromUrl ?? '');
  }, [location.search]);

  useEffect(() => {
    if (id) {
      dispatch(updateField({ field: 'id', value: id }));
    } else {
      dispatch(updateField({ field: 'id', value: null }));
    }
  }, [id, dispatch]);

  const { data: _contract, isFetching: isContractsLoading } = useQuery({
    queryKey: ['contracts', id],
    queryFn: () =>
      ContractService.search({
        ids: [id!],
        isActive: true,
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractWorkPlaceIncluded: true,
        isAccountManagerIncluded: true,
        isAccountSaleIncluded: true,
        isContractInterventionSettingIncluded: true,
      }).then((data) => {
        const contract = data?.response?.[0];
        if (!contract) {
          return null;
        }

        dispatch(setContract(contract));
        dispatch(
          updateField({
            field: 'workPlaceIntervention',
            value:
              contract?.contractInterventionSetting?.contractWorkPlaceInterventions?.map((cw: any) => cw.workPlace) ?? [],
          })
        );
        return contract;
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _offer, isFetching: isOffersLoading } = useQuery({
    queryKey: ['offers'],
    queryFn: () =>
      OfferService.search({
        companyId: reduxContract?.companyId,
        isActive: true,
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isOfferWorkPlaceIncluded: true,
        isAccountManagerIncluded: true,
        isAccountSaleIncluded: true,
      }).then((data) => {
        dispatch(setOffers(data?.response));
        return data?.response;
      }),
    enabled: !!reduxContract?.companyId,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: companies, isFetching: isCompaniesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isAgentIncluded: true,
        isWorkPlacesIncluded: true,
        isAccountManagerIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setCompanies(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _status, isFetching: isStatusLoading } = useQuery({
    queryKey: ['contractStatuses'],
    queryFn: () =>
      ConfigurationService.searchContractStatus({}).then((data) => {
        dispatch(setStatusContractes(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _contractInterventionTypes, isFetching: isContractInterventionTypeLoading } = useQuery({
    queryKey: ['contractInterventionTypes'],
    queryFn: () =>
      ConfigurationService.searchContractInterventionTypes({}).then((data) => {
        dispatch(setContractInterventionTypes(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _contractInterventionTariffTypes, isFetching: isContractInterventionTariffTypeLoading } = useQuery({
    queryKey: ['contractInterventionTariffTypes'],
    queryFn: () =>
      ConfigurationService.searchContractInterventionTariffType({}).then((data) => {
        dispatch(setContractInterventionTariffTypes(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _systemTypes, isFetching: isSystemTypesLoading } = useQuery({
    queryKey: ['systemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isDeviceTypeIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setSystemTypes(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _deviceTypes, isFetching: isDeviceTypesLoading } = useQuery({
    queryKey: ['deviceTypes'],
    queryFn: () =>
      DeviceTypeService.search({
        isSystemTypeIncluded: true,
        isLaborIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setDevices(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { mutate: saveContract, isPending: isSavingContract } = useMutation({
    mutationFn: (formData: any) => ContractService.upsert(id, formData),
    onSuccess: (data: any) => {
      if (data) {
        data?.response && navigate(`/service-maintenance/contract/edit?id=${data?.response?.id}`);
        handleResponse(data);
        if (!!id) {
          //searchComments();
        }
      } else {
        console.error('Received data is not in JSON format or JSON parsing failed.');
      }
    },
  });

  const { mutate: updateWorkPace } = useMutation({
    mutationFn: (formData: any) => WorkPlaceService.upsert(formData.id, formData),
    onSuccess: () => {},
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const onSaveContract = async () => {
    saveContract({
      ...reduxContract,
      id: id != null ? id : '',
      contractComments: reduxContract?.contractCommentsTab,
    });
  };

  const filterAndGroupData = (data: any) => {
    const result: any = {};

    data.forEach((item: any) => {
      const { workPlace, workPlaceId, systemTypeId, deviceTypeId, piece, isAnotherProvider, details } = item;

      if (!result[workPlaceId]) {
        result[workPlaceId] = {
          workPlace: workPlace,
          workPlaceId: workPlaceId,
          devices: [],
        };
      }

      result[workPlaceId].devices.push({
        systemTypeId,
        deviceTypeId,
        piece,
        isAnotherProvider,
        details,
      });
    });

    return Object.values(result);
  };

  const handleSaveContract = async () => {
    try {
      modal.confirm({
        title: 'Confirm Update',
        content: 'Sigur doriți să actualizați punctele de lucru cu configurația curentă?',
        okText: 'Da',
        cancelText: 'Nu',
        onOk: async () => {
          const workPlaceItems = filterAndGroupData(addedDevices);
          workPlaceItems.forEach((workPlaceItem: any) => {
            const formData = {
              ...workPlaceItem?.workPlace,
              agents:
                companies
                  .find((c: any) => c.id == workPlaceItem?.workPlace?.companyId)
                  .workPlaces.find((wp: any) => wp.id == workPlaceItem?.workPlace?.id)?.agents ?? [],
              contactPersons:
                companies
                  .find((c: any) => c.id == workPlaceItem?.workPlace?.companyId)
                  .workPlaces.find((wp: any) => wp.id == workPlaceItem?.workPlace?.id)?.contactPersons ?? [],
              workPlaceItems: workPlaceItem.devices.map((d: any) => {
                return {
                  id: '',
                  workPlaceId: workPlaceItem?.workPlaceId,
                  deviceTypeId: d?.deviceTypeId,
                  systemTypeId: d?.systemTypeId,
                  details: d?.details ?? '',
                  piece: d?.piece,
                  metadata: '{}',
                  isAnotherProvider: d?.isAnotherProvider ?? false,
                };
              }),
            };
            updateWorkPace(formData);
          });
          await onSaveContract();
        },
        onCancel: async () => {
          await onSaveContract();
        },
      });
    } catch (errorInfo: any) {
      message.error(errorInfo?.errorFields[0]?.errors);
    }
  };

  const handleResponse = (data: any) => {
    if (data?.errors?.length > 0) {
      data?.errors?.forEach((item: any) => {
        message.error(item);
      });
    } else {
      message.success(
        <span>
          <span className='font-weight-bold'>{data?.response?.number}</span> a fost {!!id ? 'modificat' : 'adaugat'} cu
          success
        </span>
      );
    }
  };

  const { mutate: printContractToPdf, isPending: isPrintinContract } = useMutation({
    mutationFn: () =>
      ContractService.print({
        ids: [id!],
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractWorkPlaceIncluded: true,
        isAccountManagerIncluded: true,
        isAccountSaleIncluded: true,
      }),
    onSuccess: (response: any) => {
      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf'; // Default filename if header is missing

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      return response.blob().then((blob: any) => {
        saveBlobCrossPlatform(blob, filename);
      });
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  useEffect(() => {
    if (addedDevices?.length > 0) {
      const totalLabor = Number.parseFloat(
        addedDevices
          .reduce(
            (total: any, item: any) =>
              total +
              item.piece *
                item.laborPrice *
                (item.period === 'lunar'
                  ? 12
                  : item.period === 'trimestrial'
                  ? 4
                  : item.period === 'semestrial'
                  ? 2
                  : item.period === 'anual'
                  ? 1
                  : 1),
            0
          )
          .toFixed(2)
      );

      if (addedDevices?.length > 0 && addedDevices.some((ad: any) => ad.period === 'la cerere')) {
        const groupedDevices = addedDevices.reduce((acc: any, device: any) => {
          const key = `${device?.systemType.name}-${device?.unitMeasure}-${device?.period}-${device?.workPlaceId}`;

          if (!acc[key]) {
            acc[key] = {
              systemType: device?.systemType.name,
              periodicity: device?.period,
              unitMeasure: device?.unitMeasure,
              interventionPriceOnRequest: 0,
              totalPrice: 0,
              workPlaceId: device?.workPlaceId,
              count: 0,
            };
          }

          acc[key].interventionPriceOnRequest += device?.interventionPriceOnRequest ?? 0;
          acc[key].totalPrice += device?.laborPrice;
          acc[key].count += 1;

          return acc;
        }, {});

        // Calculate the averages and transform into an array
        const result = Object.values(groupedDevices).map((group: any) => ({
          ...group,
          interventionPriceOnRequest: group?.interventionPriceOnRequest / group?.count,
        }));

        const totalInterventionPriceOnRequest = result.reduce(
          (acc, group) => acc + group?.interventionPriceOnRequest,
          0
        );
        setTotalContractValue(totalInterventionPriceOnRequest + (reduxContract?.isExpedite == true ? 250 : 0));
      } else {
        setTotalContractValue(totalLabor + (reduxContract?.isExpedite == true ? 250 : 0));
      }
    } else {
      setTotalContractValue(0);
    }
  }, [addedDevices, reduxContract.isExpedite]);

  const tabs: (Tab | false)[] = [
    {
      key: 'companyTab',
      label: 'Detalii Client',
      children: <CompanyDetailsTab />,
    },
    isShowAddEquipmentTab && {
      key: 'deviceTab',
      label: 'Adaugare Echipament',
      children: <AddEquipmentTab />,
    },
    addedDevices?.length! > 0 && {
      key: 'summaryTab',
      label: (
        <>
          Vizualizare Echipamente Adaugate
          <Badge
            color='blue'
            style={{ marginLeft: '5px' }}
            count={addedDevices?.length! > 0 ? addedDevices?.length : ''}
          ></Badge>
        </>
      ),
      children: <SummaryTab />,
    },
    addedDevices?.length! > 0 &&
      addedDevices.some((ad: any) => ad.period !== 'la cerere') && {
        key: 'manoperaTab',
        label: 'Manopera',
        children: <LaborTab />,
      },
    addedDevices?.length! > 0 && {
      key: 'interventions',
      label: 'Interventii',
      children: <InterventiiTab />,
    },
    addedDevices?.length! > 0 && {
      key: 'mentenanceTab',
      label: 'Mentenante',
      children: <Mentenance />,
    },
  ];

  const filteredTabs: Tab[] = tabs.filter((tab): tab is Tab => tab !== false);

  if (
    isSavingContract ||
    isPrintinContract ||
    isContractsLoading ||
    isOffersLoading ||
    isCompaniesLoading ||
    isStatusLoading ||
    isSystemTypesLoading ||
    isDeviceTypesLoading ||
    isUsersLoading ||
    isContractInterventionTypeLoading ||
    isContractInterventionTariffTypeLoading
  ) {
    return <Spin size='large' className='spinner' />;
  }

  return (
    <>
      <div className='topLine'>
        <h2> {id ? 'Contract Mentenanta - ' + reduxContract?.number : 'Contract Nou Mentenanta'}</h2>
        <div className='totalLine'>
          <h3>
            {reduxContract?.isExpedite ? (
              <span style={{ color: 'red' }}>Urgent Total - {totalContractValue}</span>
            ) : (
              'Total - ' + totalContractValue
            )}
          </h3>
          <div className='buttons'>
            <Button onClick={() => handleSaveContract()} className='saveBtn'>
              {isMobile ? (
                <Tooltip title='Salveaza Contract'>
                  <SaveOutlined />
                </Tooltip>
              ) : (
                'Salveaza Contract'
              )}
            </Button>

            {addedDevices?.length! > 0 && (
              <Button className='downloadBtn' onClick={() => printContractToPdf()}>
                {isMobile ? (
                  <Tooltip title='Descărcare PDF'>
                    <DownloadOutlined />
                  </Tooltip>
                ) : (
                  <>
                    {' '}
                    <DownloadOutlined /> Descărcare PDF{' '}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      <Tabs
        className='tabs'
        defaultActiveKey='1'
        items={filteredTabs}
        // tabPosition={isMobile || isTab ? 'top' : 'left'}
        tabBarStyle={{ backgroundColor: '#DADBDD' }}
        tabBarGutter={18}
      />
    </>
  );
};

export default NewContract;
